import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, Observable, throwError } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { SecureStorageService } from '../../../core/services/secure-storage.service';
import { RequestModel } from '../models/request-model';

@Injectable({
  providedIn: 'root',
})
export class RequestService {

  constructor(
    private http: HttpClient,
    private secureStorage: SecureStorageService
  ) { }

  private readonly gatewayUrl = environment.loyaltyApiUrl;

  private gatewayHeaders(skipLoader = false): HttpHeaders {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${this.secureStorage.getItem('authToken') || ''}`,
    });
    if (skipLoader) headers = headers.set('X-Skip-Loader', 'true');
    return headers;
  }

  private callAction<T = any>(actioncode: number, data: Record<string, any> = {}, skipLoader = false): Observable<T> {
    return this.http
      .post<any>(this.gatewayUrl, { actioncode, data }, { headers: this.gatewayHeaders(skipLoader) })
      .pipe(
        map((res) => {
          if (!res || res.isSuccess === false) {
            const message = res?.message || 'Request failed.';
            throw { message, error: { message, code: res?.code }, status: 400 };
          }
          return res.data as T;
        }),
        catchError((err) => throwError(() => err)),
      );
  }

  /** action 8161 · admin-contact-request-list (source='contact'). */
  getRequests(params?: any, skipLoader = false): Observable<RequestModel[]> {
    return this.callAction<any>(8161, { ...(params ?? {}), source: 'contact' }, skipLoader).pipe(
      catchError((error) => throwError(() => new Error(error?.message || 'Failed to load contact requests.')))
    );
  }

  /** action 8161 · admin-contact-request-list (source='partner'). Same table as Contact Us. */
  getPartnerRequests(params?: any, skipLoader = false): Observable<any> {
    return this.callAction<any>(8161, { ...(params ?? {}), source: 'partner' }, skipLoader).pipe(
      catchError((error) => throwError(() => new Error(error?.message || 'Failed to load partner requests.')))
    );
  }

  /**
   * action 8099 · admin-cash-subscription-list — the cash-payment approval queue, against
   * `subscriptions` (provider='cash') instead of the legacy user.in_active_subid. Reshaped here to
   * the legacy field names (user_id/user_name/user_email/user_phone/plan_type) the grid's
   * valueGetters already fall back to, so the component itself needed no changes.
   * See backend-microservices/src/admin/AdminSubscription/AdminSubscription.service.ts.
   */
  getCashSubscriptions(params?: any, skipLoader = false): Observable<any> {
    // Fetches the full history (any lifecycle status) — the backend's own `status` param means
    // pending/active/expired, not the paid/unpaid the grid's filter dropdown means, so that
    // filter is applied here instead, over the reshaped payment_status.
    return this.callAction<any>(8099, { page: 1, per_page: 100000, status: 'all' }, skipLoader).pipe(
      map((res: any) => {
        let items = (res?.subscriptions || []).map((s: any) => ({
          ...s,
          user_id: s.client_id,
          user_name: s.client_name,
          user_email: s.client_email,
          user_phone: s.phone_number,
          plan_type: s.plan_name,
          // 8099 derives payment_status from the row's current status (active → paid), so a cash
          // plan the admin already approved flips back to "unpaid" once a later plan change
          // expires it. A cash row only gets activated_at when it is approved, so that marks it paid.
          payment_status: s.payment_status === 'paid' || s.activated_at ? 'paid' : (s.payment_status || 'unpaid'),
          billing_cycle: this.cashBillingCycle(s),
        }));
        if (params?.payment_status) {
          items = items.filter((s: any) => s.payment_status === params.payment_status);
        }
        if (params?.plan_type) {
          const wanted = String(params.plan_type).toLowerCase();
          items = items.filter((s: any) => String(s.plan_type || '').toLowerCase() === wanted);
        }
        return { items };
      }),
      catchError((error) => {
        return throwError(
          () => new Error(error?.message || 'Failed to load cash subscriptions.')
        );
      })
    );
  }

  /**
   * Monthly vs yearly for a cash request. `subscriptions.billing_cycle` ('monthly' | 'annually')
   * is the source of truth; older rows without it fall back to the length of the billed period.
   * `type` is only used when it holds a cycle — on this queue it normally means the payment type.
   */
  private cashBillingCycle(s: any): 'monthly' | 'annually' | '' {
    const raw = String(
      s?.billing_cycle ?? s?.billing_type ?? s?._subscription_plan?.mode ?? s?.plan_attributes?.billing_cycle ??
      (/^(monthly|annually|yearly|annual)$/i.test(String(s?.type ?? '')) ? s.type : '')
    ).trim().toLowerCase();
    if (raw === 'monthly' || raw === 'month') return 'monthly';
    if (['annually', 'annual', 'yearly', 'year'].includes(raw)) return 'annually';

    const start = new Date(s?.period_start ?? s?.start_date ?? '').getTime();
    const end = new Date(s?.period_end ?? s?.end_date ?? '').getTime();
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return '';
    return (end - start) / 86_400_000 >= 300 ? 'annually' : 'monthly';
  }

  /**
   * action 8100 · admin-cash-approve — approve or reject the newest pending cash request for this
   * client. A Web QR row (product 'web_qr') is decided by its own subscription_id instead, since the
   * same client can also have a B2C request open.
   */
  updateCashApproval(payload: {
    user_id: number;
    isApprove: boolean;
    message: string;
    subscription_id?: string;
    product?: string;
  }): Observable<any> {
    return this.callAction<any>(8100, {
      client_id: payload.user_id,
      isApprove: payload.isApprove,
      message: payload.message,
      ...(payload.product === 'web_qr'
        ? { product: 'web_qr', subscription_id: payload.subscription_id }
        : {}),
    }).pipe(
      catchError((error) => {
        return throwError(
          () => new Error(error?.message || 'Failed to update cash approval.')
        );
      })
    );
  }

  /** action 8167 · admin-sdk-grant-list. */
  getSdkCashRequests(params?: any, skipLoader = false): Observable<any> {
    return this.callAction<any>(8167, params ?? {}, skipLoader).pipe(
      catchError((error) => throwError(() => new Error(error?.message || 'Failed to load SDK cash requests.')))
    );
  }

  /** action 8168 · admin-sdk-grant-update. */
  updateSdkConfig(payload: any): Observable<any> {
    return this.callAction<any>(8168, payload).pipe(
      catchError((error) => throwError(() => new Error(error?.message || 'Failed to update SDK config.')))
    );
  }

  /** action 8165 · admin-online-pending-list. */
  getPendingOnlinePayments(params?: any, skipLoader = false): Observable<any> {
    return this.callAction<any>(8165, params ?? {}, skipLoader).pipe(
      catchError((error) => throwError(() => new Error(error?.message || 'Failed to load pending online payments.')))
    );
  }

  /** action 8166 · admin-online-pending-update. */
  updatePendingOnlinePayment(payload: { id: number; payment_status: string }): Observable<any> {
    return this.callAction<any>(8166, payload).pipe(
      catchError((error) => throwError(() => new Error(error?.message || 'Failed to update online payment.')))
    );
  }

  /** action 8162 · admin-contact-request-update-status. Shared by the Contact and Partner tabs. */
  updateContactRequestStatus(payload: {
    id: number | string;
    status: string;
  }): Observable<any> {
    // request_id mirrors id so the action finds the row whichever key it reads.
    return this.callAction<any>(8162, { ...payload, request_id: payload.id }).pipe(
      catchError((error) => throwError(() => new Error(error?.message || 'Failed to update request status.')))
    );
  }



}
